import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Wrench,
  MapPin,
  Ruler,
  Database,
  Calculator,
  Save,
  AlertTriangle,
} from 'lucide-react';

// Map each tool name to a color palette so users can tell tools apart at a glance.
const TOOL_PALETTE = {
  calculate_emission: { bar: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: 'text-emerald-600' },
  log_trip:           { bar: 'bg-blue-500',    badge: 'bg-blue-50 text-blue-800 border-blue-200',       icon: 'text-blue-600' },
  list_my_trips:      { bar: 'bg-violet-500',  badge: 'bg-violet-50 text-violet-800 border-violet-200', icon: 'text-violet-600' },
  travel_footprint:   { bar: 'bg-amber-500',   badge: 'bg-amber-50 text-amber-800 border-amber-200',    icon: 'text-amber-600' },
  delete_trip:        { bar: 'bg-red-500',     badge: 'bg-red-50 text-red-800 border-red-200',          icon: 'text-red-600' },
  get_modes:          { bar: 'bg-slate-500',   badge: 'bg-slate-100 text-slate-800 border-slate-200',   icon: 'text-slate-600' },
};

const DEFAULT_PALETTE = { bar: 'bg-slate-500', badge: 'bg-slate-100 text-slate-800 border-slate-200', icon: 'text-slate-600' };

function paletteFor(toolName) {
  return TOOL_PALETTE[toolName] || DEFAULT_PALETTE;
}

// Colorize parameter values by JS type so reads stay at a glance:
//   string → amber, number → blue, boolean → violet, null → slate italic
function ValueSpan({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-slate-400 italic">null</span>;
  }
  if (typeof value === 'string') {
    return <span className="text-amber-700">"{value}"</span>;
  }
  if (typeof value === 'number') {
    return <span className="text-blue-700">{value}</span>;
  }
  if (typeof value === 'boolean') {
    return <span className="text-violet-700">{String(value)}</span>;
  }
  // object/array — render compact JSON
  try {
    return <span className="text-slate-600">{JSON.stringify(value)}</span>;
  } catch {
    return <span className="text-slate-600">{String(value)}</span>;
  }
}

function ResultPreview({ result }) {
  if (result === null || result === undefined) return null;
  // MISSING_FIELD errors surface as a plain "asked for X" line — quieter than raw JSON.
  if (typeof result === 'object' && result.MISSING_FIELD) {
    return (
      <div className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1">
        <span className="font-medium">Needs input:</span>{' '}
        {result.ask_user_exactly_this || 'missing field'}
      </div>
    );
  }
  if (typeof result === 'object' && result.error) {
    return (
      <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
        <span className="font-medium">Error:</span> {String(result.error).slice(0, 200)}
      </div>
    );
  }
  // Highlight co2e_kg when the tool returned a calculation.
  if (typeof result === 'object' && result.co2e_kg != null) {
    return (
      <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-center gap-2">
        <span className="font-semibold">{Number(result.co2e_kg).toFixed(2)} kg CO₂e</span>
        {result.activity_value != null && (
          <span className="text-slate-600">
            · {Number(result.activity_value).toFixed(1)} {result.activity_unit || 'km'}
          </span>
        )}
      </div>
    );
  }
  let text;
  try {
    text = JSON.stringify(result, null, 2);
  } catch {
    text = String(result);
  }
  return (
    <pre className="mt-2 text-[11px] leading-snug text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1 overflow-x-auto whitespace-pre-wrap max-h-48">
      {text.length > 1200 ? text.slice(0, 1200) + '\n…' : text}
    </pre>
  );
}

// Only these kinds render in the timeline. Request-phase and lookup-phase steps
// are dropped — the response/found steps carry everything useful in one line.
const VISIBLE_KINDS = new Set([
  'geocode_response', 'geocode_skipped', 'geocode_error',
  'distance_response',
  'factor_found', 'factor_not_found',
  'compute', 'save',
]);

const SUBSTEP_META = {
  geocode_response:  { label: 'Located',            Icon: MapPin,        color: 'text-sky-700' },
  geocode_skipped:   { label: 'Geocode skipped',    Icon: AlertTriangle, color: 'text-slate-500' },
  geocode_error:     { label: 'Geocode failed',     Icon: AlertTriangle, color: 'text-red-600' },
  distance_response: { label: 'Distance',           Icon: Ruler,         color: 'text-cyan-700' },
  factor_found:      { label: 'Factor matched',     Icon: Database,      color: 'text-indigo-700' },
  factor_not_found:  { label: 'No factor matched',  Icon: AlertTriangle, color: 'text-red-600' },
  compute:           { label: 'Computed',           Icon: Calculator,    color: 'text-emerald-700' },
  save:              { label: 'Saved',              Icon: Save,          color: 'text-blue-700' },
};

// Short, human phrases — no lat/lng, no engine names, no factor values, rounded km.
function shortCity(addr) {
  if (!addr) return '';
  return String(addr).split(',')[0].trim();
}

const SUBSTEP_HEADLINE = {
  geocode_response:  (s) => s.found
    ? shortCity(s.formatted_address)
    : `"${s.address || ''}" not found`,
  distance_response: (s) => s.distance_km != null
    ? `~${Math.round(Number(s.distance_km))} km`
    : '',
  factor_found:      (s) => s.display_name || '',
  factor_not_found:  () => '',
  compute:           (s) => s.co2e_kg != null
    ? `${Math.round(Number(s.co2e_kg))} kg CO₂e`
    : '',
  save:              () => 'trip recorded',
  geocode_skipped:   () => '',
  geocode_error:     () => '',
};

function Substep({ step, isLast }) {
  const meta = SUBSTEP_META[step.kind] || {
    label: step.kind, Icon: Wrench, color: 'text-slate-600',
  };
  const headline = (SUBSTEP_HEADLINE[step.kind] || (() => ''))(step);

  return (
    <div className="flex gap-2 relative">
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-5 h-5 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center">
          <meta.Icon className={`w-3 h-3 ${meta.color}`} />
        </div>
        {!isLast && <div className="flex-1 w-px bg-slate-200 min-h-[10px]" />}
      </div>
      <div className="flex-1 pb-2 min-w-0 flex items-baseline gap-1.5 flex-wrap">
        <span className={`text-[11px] font-medium ${meta.color}`}>{meta.label}</span>
        {headline && (
          <span className="text-[11px] text-slate-600 truncate">{headline}</span>
        )}
      </div>
    </div>
  );
}

export default function ToolCallCard({ step, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const palette = paletteFor(step.name);
  const argEntries = Object.entries(step.args || {}).filter(([, v]) => v !== null && v !== undefined);
  const isMissing = step.result && typeof step.result === 'object' && step.result.MISSING_FIELD;
  const substeps = (Array.isArray(step.substeps) ? step.substeps : [])
    .filter((s) => VISIBLE_KINDS.has(s.kind));

  return (
    <div className={`rounded-md border border-slate-200 bg-white overflow-hidden shadow-sm`}>
      <div className={`h-0.5 ${palette.bar}`} />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-slate-50 transition-colors"
      >
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
        )}
        <Wrench className={`w-3 h-3 ${palette.icon}`} />
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-medium border ${palette.badge}`}>
          {step.name}
        </span>
        <span className="text-slate-500 truncate flex-1 text-left">
          {argEntries.length === 0
            ? '()'
            : `(${argEntries.map(([k]) => k).join(', ')})`}
        </span>
        {substeps.length > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
            {substeps.length} step{substeps.length === 1 ? '' : 's'}
          </span>
        )}
        {isMissing && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">
            needs input
          </span>
        )}
      </button>

      {open && (
        <div className="px-3 pb-2.5 pt-1 text-xs space-y-2">
          {argEntries.length > 0 ? (
            <div className="font-mono space-y-0.5">
              {argEntries.map(([key, value]) => (
                <div key={key} className="flex">
                  <span className="text-fuchsia-700 mr-1.5">{key}</span>
                  <span className="text-slate-400 mr-1.5">=</span>
                  <ValueSpan value={value} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-slate-400 italic">no arguments</div>
          )}
          {substeps.length > 0 && (
            <div className="pt-1 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">
                What happened
              </div>
              <div className="pl-0.5">
                {substeps.map((s, i) => (
                  <Substep key={i} step={s} isLast={i === substeps.length - 1} />
                ))}
              </div>
            </div>
          )}
          <ResultPreview result={step.result} />
        </div>
      )}
    </div>
  );
}
